import React, { useCallback, useRef } from "react";
import { ColumnLayout } from "@amzn/awsui-components-react-v3";
import {
  FieldGroupDto,
  FieldSchemaDto,
  EditComponentErrorResponse,
} from "@amzn/crowd-front-end-common-library";
import { FormContextProvider } from "../../forms";
import FormFieldRenderer from "../../forms/FormFieldRenderer/FormFieldRenderer";
import { EditTeamResult } from "./EditTeamResult";
import styles from "../editTeam.module.css";
import { MetricsState } from "../../logging";
import teamFormSchema from "../../common/schema/teamFormSchema";

interface EditTeamFormProps {
  originalTeam: EditTeamResult;
  metrics: MetricsState;
  ownerName?: string;
  validationErrors: EditComponentErrorResponse | undefined;
  detectChangesInForm: (diff: Partial<EditTeamResult>) => void;
}

const EditTeamForm: React.FC<EditTeamFormProps> = ({
  originalTeam,
  metrics,
  ownerName,
  validationErrors,
  detectChangesInForm,
}): JSX.Element => {
  const updateLocalChanges = useCallback(
    (_: Partial<EditTeamResult>, diff: Partial<EditTeamResult>) => {
      detectChangesInForm(diff);
    },
    [detectChangesInForm]
  );

  /**
   *
   * Apply useRef for originalTeam to avoid edit team form getting reset because of the originalTeam reference change
   *
   * NOTE: Using useRef might cause a potential bug.
   * If the content of originalTeam changes but the EditTeamForm component is not totally unmounted and remounted,
   * useRef would use stale data which causes the form not showing the correct content.
   *
   */
  const savedOriginalTeam = useRef(originalTeam);

  const schema = teamFormSchema;

  const teamGroup = schema.groups.find(
    (group) => group.groupLabel === "Team edit information"
  ) as FieldGroupDto;
  const renderedFieldDefinitions = teamGroup.fieldDefinitions;

  const getFieldRenderer = (field: FieldSchemaDto): JSX.Element => {
    if (field.label === "Team owner") {
      const descriptionText = (
        <React.Fragment>
          <div className={styles["info-text"]}>
            {ownerName
              ? `Changing the team owner will revoke ${ownerName}'s admin permissions and grant admin permissions to the new owner.`
              : "The team owner will be granted admin access."}
          </div>
        </React.Fragment>
      );
      return (
        <FormFieldRenderer
          key={field.propertyName}
          field={field}
          customLabel={descriptionText}
        />
      );
    }

    return (
      <FormFieldRenderer
        key={field.propertyName}
        field={field}
        dispatchOnValueUpdate
      />
    );
  };

  return (
    <React.Fragment>
      <div className={styles["update-step-wrapper"]}>
        <FormContextProvider<Partial<EditTeamResult>>
          metrics={metrics}
          formId="editTeam-page"
          onFormValueChange={updateLocalChanges}
          validationErrors={validationErrors}
          savedValue={savedOriginalTeam.current}
        >
          <ColumnLayout>
            <div data-awsui-column-layout-root="true">
              {renderedFieldDefinitions.map((field) => getFieldRenderer(field))}
            </div>
          </ColumnLayout>{" "}
        </FormContextProvider>
      </div>
    </React.Fragment>
  );
};

export default EditTeamForm;
